// ═══════════════════════════════════════════════════════════════
// PACE — Reports
// Plan coverage summaries, tier breakdown, CSV/PDF export
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import {
  Paper, Box, Group, Stack, SimpleGrid, Title, Text, Button, Progress,
  Center, Loader,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import { IconDownload, IconFileText, IconPrinter, IconRefresh } from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { usePaceStore, PACE_TIERS, PACE_TIER_LABELS } from '../../store/paceStore';
import type { PACEProfile } from '../../store/paceStore';

const TIER_COLOR_MAP: Record<string, string> = {
  primary: 'var(--color-success)',
  alternate: 'var(--color-primary-700)',
  contingency: '#C68700',
  emergency: 'var(--color-error)',
};

function tierFilled(profile: PACEProfile, tier: string): boolean {
  return !!(profile[tier as keyof PACEProfile] as string)?.trim();
}

function filledCount(profile: PACEProfile): number {
  return PACE_TIERS.filter(t => tierFilled(profile, t)).length;
}

export default function Reports() {
  const { activeIncident } = useAuthStore();
  const { profiles, loading, loadProfiles } = usePaceStore();

  useEffect(() => { loadProfiles(); }, []);

  const compliance = useMemo(() => {
    const total = profiles.length;
    const complete = profiles.filter(p => filledCount(p) === 4).length;
    const incomplete = profiles.filter(p => { const f = filledCount(p); return f > 0 && f < 4; }).length;
    const empty = profiles.filter(p => filledCount(p) === 0).length;

    const byTier = PACE_TIERS.map(tier => {
      const count = profiles.filter(p => tierFilled(p, tier)).length;
      return { tier, count, percent: total > 0 ? Math.round((count / total) * 100) : 0 };
    });

    const byStatus = [
      { status: 'active', label: 'Active', count: profiles.filter(p => p.status === 'active' || !p.status).length },
      { status: 'inactive', label: 'Inactive', count: profiles.filter(p => p.status === 'inactive').length },
    ];

    return {
      total,
      complete,
      incomplete,
      empty,
      compliancePercent: total > 0 ? Math.round((complete / total) * 100) : 0,
      byTier,
      byStatus,
    };
  }, [profiles]);

  const exportCSV = () => {
    const rows = [
      ['Plan Name', 'Primary', 'Alternate', 'Contingency', 'Emergency', 'Status', 'Tiers Filled'],
      ...profiles.map(p => [
        p.profileName,
        tierFilled(p, 'primary') ? 'Yes' : 'No',
        tierFilled(p, 'alternate') ? 'Yes' : 'No',
        tierFilled(p, 'contingency') ? 'Yes' : 'No',
        tierFilled(p, 'emergency') ? 'Yes' : 'No',
        p.status || 'active',
        filledCount(p) + '/4',
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pace-plans-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    notifications.show({ message: 'CSV exported!', color: 'var(--color-success)' });
  };

  const exportPDF = () => {
    const tierColors: Record<string, string> = {
      primary: '#16a34a', alternate: '#2563eb', contingency: '#ca8a04', emergency: '#B12525',
    };
    const html = `
      <div style="background:#112D4E;color:#fff;padding:12px 16px;margin-bottom:16px;border-radius:4px;display:flex;justify-content:space-between">
        <h1 style="font-size:16px;margin:0">PACE Plan Coverage Report — ${activeIncident?.name ?? 'Incident'}</h1>
        <span style="font-size:10px;opacity:.6">${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
      </div>
      <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:16px">
        ${[
          { label: 'Total Plans', value: compliance.total, color: '#475569' },
          { label: 'Complete (4/4)', value: compliance.complete, color: '#16a34a' },
          { label: 'Compliance %', value: compliance.compliancePercent + '%', color: '#2563eb' },
          { label: 'Incomplete', value: compliance.incomplete, color: '#d97706' },
        ].map(s => `<div style="border:1px solid #e2e8f0;border-radius:6px;padding:10px;text-align:center">
          <div style="font-size:20px;font-weight:700;color:${s.color}">${s.value}</div>
          <div style="font-size:9px;color:#7B7D7F;text-transform:uppercase">${s.label}</div>
        </div>`).join('')}
      </div>
      <h2 style="font-size:14px;color:#3F72AF;margin:16px 0 8px">Coverage by Tier</h2>
      ${compliance.byTier.map(t => `
        <div style="margin-bottom:8px">
          <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:2px">
            <strong>${PACE_TIER_LABELS[t.tier]}</strong>
            <span>${t.count}/${compliance.total} (${t.percent}%)</span>
          </div>
          <div style="height:6px;background:#f1f5f9;border-radius:3px;overflow:hidden">
            <div style="height:100%;width:${t.percent}%;background:${tierColors[t.tier]};border-radius:3px"></div>
          </div>
        </div>
      `).join('')}
      <h2 style="font-size:14px;color:#3F72AF;margin:16px 0 8px">Plan Details</h2>
      <table style="width:100%;border-collapse:collapse">
        <thead><tr>
          <th style="text-align:left;padding:6px 8px;background:#f1f5f9;font-size:10px;text-transform:uppercase">Plan Name</th>
          <th style="text-align:left;padding:6px 8px;background:#f1f5f9;font-size:10px;color:#16a34a">P</th>
          <th style="text-align:left;padding:6px 8px;background:#f1f5f9;font-size:10px;color:#2563eb">A</th>
          <th style="text-align:left;padding:6px 8px;background:#f1f5f9;font-size:10px;color:#ca8a04">C</th>
          <th style="text-align:left;padding:6px 8px;background:#f1f5f9;font-size:10px;color:#B12525">E</th>
          <th style="text-align:left;padding:6px 8px;background:#f1f5f9;font-size:10px">Status</th>
        </tr></thead>
        <tbody>
          ${profiles.map(p => `<tr>
            <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9"><strong>${p.profileName}</strong></td>
            ${PACE_TIERS.map(t => `<td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;text-align:center">${tierFilled(p, t) ? '✓' : '—'}</td>`).join('')}
            <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9">${p.status || 'active'}</td>
          </tr>`).join('')}
        </tbody>
      </table>`;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>PACE Report</title>
      <style>* { box-sizing: border-box; margin: 0; padding: 0; } body { font-family: sans-serif; font-size: 11px; color: #112D4E; padding: 16px; } @media print { body { -webkit-print-color-adjust: exact; } }</style>
    </head><body>${html}</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
    notifications.show({ message: 'PDF opened for printing', color: 'var(--color-success)' });
  };

  const exportContactSheet = () => {
    const html = `
      <div style="background:#112D4E;color:#fff;padding:12px 16px;margin-bottom:16px;border-radius:4px;display:flex;justify-content:space-between">
        <h1 style="font-size:16px;margin:0">PACE Contact Sheet — ${activeIncident?.name ?? 'Incident'}</h1>
        <span style="font-size:10px;opacity:.6">${new Date().toLocaleDateString()}</span>
      </div>
      <p style="color:#7B7D7F;font-size:11px;margin-bottom:16px">${profiles.length} plans — CONFIDENTIAL: For authorized personnel only</p>
      ${profiles.map(p => `
        <div style="margin-bottom:16px;padding:12px;border:1px solid #e2e8f0;border-radius:6px;break-inside:avoid">
          <div style="font-weight:700;font-size:13px;margin-bottom:8px">${p.profileName}</div>
          ${PACE_TIERS.map(t => {
            const val = (p[t as keyof typeof p] as string) || '';
            return `<div style="display:flex;gap:8px;margin-bottom:4px">
              <span style="width:90px;font-size:10px;font-weight:600;text-transform:uppercase;color:#7B7D7F;flex-shrink:0">${PACE_TIER_LABELS[t]}</span>
              <span style="font-size:11px">${val || '—'}</span>
            </div>`;
          }).join('')}
          ${p.notes ? `<div style="margin-top:6px;font-size:10px;color:#939598;border-top:1px solid #f1f5f9;padding-top:6px">${p.notes}</div>` : ''}
        </div>
      `).join('')}`;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>PACE Contact Sheet</title>
      <style>* { box-sizing: border-box; margin: 0; padding: 0; } body { font-family: sans-serif; font-size: 11px; color: #112D4E; padding: 16px; } @media print { body { -webkit-print-color-adjust: exact; } .page-break { page-break-after: always; } }</style>
    </head><body>${html}</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
    notifications.show({ message: 'Contact sheet opened for printing', color: 'var(--color-success)' });
  };

  if (loading && profiles.length === 0) {
    return <Center py="xl" mih={256}><Loader /></Center>;
  }

  return (
    <Box>
      <Group justify="space-between" mb="lg">
        <Box>
          <Title order={3} c="black">Compliance Reports</Title>
          <Text c="dimmed" size="sm" mt={4}>Generated {new Date().toLocaleDateString()}</Text>
        </Box>
        <Group gap="xs">
          <Button variant="outline" size="sm" onClick={() => loadProfiles()} leftSection={<IconRefresh size={12} />}>
            Refresh
          </Button>
          <Button size="sm" onClick={exportCSV} leftSection={<IconDownload size={12} />}>CSV</Button>
          <Button size="sm" color="var(--color-error)" onClick={exportPDF} leftSection={<IconFileText size={12} />}>PDF</Button>
          <Button size="sm" color="#939598" onClick={exportContactSheet} leftSection={<IconPrinter size={12} />}>
            Contact Sheet
          </Button>
        </Group>
      </Group>

      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
        {[
          { label: 'Total Plans', value: compliance.total, color: '#939598' },
          { label: 'Complete (4/4)', value: compliance.complete, color: 'var(--color-success)' },
          { label: 'Coverage', value: `${compliance.compliancePercent}%`, color: 'var(--color-primary-700)' },
          { label: 'Incomplete', value: compliance.incomplete, color: '#C68700' },
        ].map(s => (
          <Paper key={s.label} p="sm" withBorder ta="center" style={{ borderTop: `3px solid ${s.color}` }}>
            <Text size="xs" fw={500} tt="uppercase" c="dimmed">{s.label}</Text>
            <Text fz={28} fw={700} mt={2} style={{ color: s.color === '#939598' ? undefined : s.color }}>{s.value}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      <Paper p="lg" withBorder mb="md">
        <Text fw={600} mb="md">Coverage by PACE Tier</Text>
        <Stack gap="sm">
          {compliance.byTier.map(({ tier, count, percent }) => (
            <Box key={tier}>
              <Group justify="space-between" mb={4}>
                <Text size="sm" fw={500}>{PACE_TIER_LABELS[tier]}</Text>
                <Text size="sm" c="dimmed">{count} / {compliance.total} ({percent}%)</Text>
              </Group>
              <Progress value={percent} color={TIER_COLOR_MAP[tier]} size="sm" radius="xl" />
            </Box>
          ))}
        </Stack>
      </Paper>

      <Paper p="lg" withBorder>
        <Text fw={600} mb="md">Plan Details</Text>
        {profiles.length === 0 ? (
          <Text c="dimmed" size="sm" fs="italic">No plans available.</Text>
        ) : (
          <Stack gap="xs">
            {profiles.map(p => {
              const filled = filledCount(p);
              return (
                <Group key={p.id} p="xs" style={{ borderBottom: '1px solid #e2e8f0' }} justify="space-between">
                  <Text size="sm" fw={500} style={{ flex: 1 }}>{p.profileName || '(Unnamed)'}</Text>
                  <Group gap={4}>
                    {PACE_TIERS.map(t => (
                      <Box
                        key={t}
                        w={6} h={16}
                        title={PACE_TIER_LABELS[t]}
                        style={{
                          borderRadius: 4,
                          background: tierFilled(p, t)
                            ? TIER_COLOR_MAP[t]
                            : '#e2e8f0',
                        }}
                      />
                    ))}
                    <Text size="xs" c="dimmed" ml={4}>{filled}/4</Text>
                  </Group>
                </Group>
              );
            })}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
